import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { getReceivables } from "../../core/api/reportService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

// The /api/reports/receivables endpoint accepts no query params — it always returns the
// full current snapshot. "As of Date" filtering is therefore applied client-side below.
// Field names below mirror the confirmed live shape of /api/reports/payables (same report
// family, e.g. no daysOverdue/ageBucket fields exist) — receivables itself returned an
// empty snapshot when last checked live, so this is inferred by close analogy, not directly verified.
const Receivables = () => {
  const [allData, setAllData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asOf, setAsOf] = useState(today());

  const runReport = async () => {
    setLoading(true);
    try {
      const res = await getReceivables();
      const payload = res.data || {};
      setAllData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load receivables."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAsOf(today());
  };

  const data = useMemo(() => allData.filter((r) =>
    !asOf || !r.invoiceDate || r.invoiceDate.split("T")[0] <= asOf
  ), [allData, asOf]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const columns = [
    { title: "Patient", dataIndex: "patientName", sorter: (a, b) => (a.patientName || "").localeCompare(b.patientName || "") },
    { title: "OP Number", dataIndex: "oPNumber", width: 110 },
    { title: "Invoice #", dataIndex: "invoiceNumber", width: 120 },
    { title: "Invoice Date", dataIndex: "invoiceDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Total Amount", dataIndex: "totalAmount", align: "right", width: 130, render: fmt },
    { title: "Paid", dataIndex: "paidAmount", align: "right", width: 110, render: (v) => <span className="text-success">{fmt(v)}</span> },
    {
      title: "Balance", dataIndex: "balanceAmount", align: "right", width: 120,
      render: (v) => <span className="text-danger fw-semibold">{fmt(v)}</span>,
      sorter: (a, b) => (b.balanceAmount || 0) - (a.balanceAmount || 0),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Receivables Report</h4><h6>Outstanding dues from patients</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">As of Date</label>
                <input type="date" className="form-control form-control-sm" value={asOf}
                  onChange={(e) => setAsOf(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total Receivable", value: fmt(summary.totalReceivable), color: "danger" },
              { label: "0–30 Days", value: fmt(summary.bucket030), color: "success" },
              { label: "31–60 Days", value: fmt(summary.bucket3160), color: "warning" },
              { label: "60+ Days", value: fmt(summary.bucket60Plus), color: "danger" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-6 fw-bold`}>{s.value}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
            {data.length > 0 && (
              <div className="mt-2 text-end">
                Total Outstanding: <strong className="text-danger ms-1">
                  {fmt(data.reduce((s, r) => s + (r.balanceAmount || 0), 0))}
                </strong>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Receivables;
